/**
 * Componentes de visualização — gauge radial e barra empilhada.
 *
 * Seguem o skill de dataviz do produto: forma escolhida pelo trabalho do dado
 * (Meter para "valor contra um limite", stacked bar para "parte-do-todo"),
 * cor validada pelo `validate_palette.js` antes de qualquer uso, marcas finas
 * com pontas arredondadas, e legenda com rótulo direto sempre que houver mais
 * de uma série.
 *
 * DESVIO DELIBERADO do spec de "Meter" do skill: o spec padrão diz que o
 * preenchimento carrega severidade (accent → warning → danger). Aqui não —
 * o app do paciente nunca mostra semáforo clínico nem zona de "perigo"
 * (build-prompt §3.1/§8.3), então o gauge usa UM hue só, sem escala de
 * severidade. Não é limitação técnica, é a mesma regra de design que já
 * governa o resto do dashboard.
 */

import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useRef, useState } from 'react';
import { color as tokens, font } from './tokens.ts';

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ------------------------------------------------------------ RadialMeter

export interface RadialMeterProps {
  /** Valor atual — a agulha/preenchimento do medidor. */
  value: number;
  min: number;
  max: number;
  /** Diâmetro em px. */
  size?: number;
  strokeWidth?: number;
  /** Hue único do preenchimento. Track é o mesmo hue, mais claro ("same-ramp"). */
  fillColor?: string;
  /**
   * Ângulo de início e abertura total, em graus, convenção matemática
   * (0° = direita, 90° = topo, sentido anti-horário).
   *
   * Gauge (semicírculo, tipo velocímetro): start=180, sweep=180.
   * Ring (círculo quase completo, tipo anel de atividade): start=90, sweep=359.9.
   */
  startAngle?: number;
  sweepAngle?: number;
  /** Texto grande no centro — o número que a pessoa lê primeiro. */
  valueLabel: string;
  /** Unidade ou legenda curta abaixo do número central. */
  unitLabel?: string;
  /** Rótulos nas pontas do arco (ex.: valor mínimo e máximo do dia). */
  startEndpointLabel?: string;
  endEndpointLabel?: string;
}

/**
 * Gauge/ring radial de série única.
 *
 * `strokeLinecap="round"` nas duas pontas do preenchimento (marca fina com
 * ponta arredondada, por especificação do skill). Sem eixo, sem grade — o
 * arco e o número central carregam toda a informação.
 */
export function RadialMeter({
  value,
  min,
  max,
  size = 160,
  strokeWidth = 12,
  fillColor = tokens.accent,
  startAngle = 180,
  sweepAngle = 180,
  valueLabel,
  unitLabel,
  startEndpointLabel,
  endEndpointLabel,
}: RadialMeterProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - strokeWidth;

  const fraction = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0;

  const point = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };

  const arcPath = (sweep: number) => {
    const from = point(startAngle);
    const to = point(startAngle - sweep);
    const largeArc = sweep > 180 ? 1 : 0;
    return `M ${from.x} ${from.y} A ${r} ${r} 0 ${largeArc} 1 ${to.x} ${to.y}`;
  };

  const isGauge = sweepAngle <= 180;
  // Só o gauge (semicírculo) reserva espaço embaixo para os rótulos de ponta;
  // o ring (quase 360°) não tem "embaixo" livre.
  const viewHeight = isGauge ? size / 2 + strokeWidth + 28 : size;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={size} height={viewHeight} viewBox={`0 0 ${size} ${viewHeight}`}>
        {/* Track: mesmo hue do preenchimento, bem mais claro — "lê como estado
            no arco inteiro" mesmo antes de saber o valor. */}
        <path
          d={arcPath(sweepAngle)}
          fill="none"
          stroke={hexToRgba(fillColor, 0.14)}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={arcPath(sweepAngle * fraction)}
          fill="none"
          stroke={fillColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Número central — a leitura primária, sempre a fonte padrão (nunca
            display/serifada) e em algarismos proporcionais, não tabulares. */}
        <text
          x={cx}
          y={isGauge ? cy - 2 : cy - (unitLabel ? 4 : -6)}
          textAnchor="middle"
          fontSize={size * 0.19}
          fontWeight={600}
          fill={tokens.text}
          fontFamily={font.stack}
        >
          {valueLabel}
        </text>
        {unitLabel && (
          <text
            x={cx}
            y={isGauge ? cy + size * 0.09 : cy + size * 0.14}
            textAnchor="middle"
            fontSize={size * 0.075}
            fill={tokens.textSecondary}
            fontFamily={font.stack}
          >
            {unitLabel}
          </text>
        )}

        {isGauge && startEndpointLabel && (
          <text
            x={point(startAngle).x}
            y={cy + strokeWidth + 16}
            textAnchor="start"
            fontSize={size * 0.08}
            fill={tokens.textSecondary}
            fontFamily={font.stack}
          >
            {startEndpointLabel}
          </text>
        )}
        {isGauge && endEndpointLabel && (
          <text
            x={point(startAngle - sweepAngle).x}
            y={cy + strokeWidth + 16}
            textAnchor="end"
            fontSize={size * 0.08}
            fill={tokens.textSecondary}
            fontFamily={font.stack}
          >
            {endEndpointLabel}
          </text>
        )}
      </svg>
    </div>
  );
}

// ------------------------------------------------------------ StackedBar

export interface StackedBarSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface StackedBarProps {
  segments: StackedBarSegment[];
  height?: number;
  /** Formata o valor de cada segmento na legenda (ex.: "1h20"). */
  formatValue?: (value: number) => string;
}

/**
 * Barra horizontal empilhada — parte-do-todo, categórico.
 *
 * Segmentos com gap de 2px entre si (a mesma regra de "surface gap" que
 * separaria fatias de um dashboard analítico), pontas arredondadas só nas
 * extremidades externas da barra inteira. Com ≤4 séries, rótulo direto é
 * obrigatório — por isso a legenda abaixo nunca é opcional aqui.
 */
export function StackedBar({ segments, height = 14, formatValue }: StackedBarProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  const gap = 2;
  const radius = height / 2;

  return (
    <div>
      <div style={{ display: 'flex', gap: `${gap}px`, height }}>
        {segments.map((segment, i) => {
          if (segment.value <= 0) return null;
          const widthPercent = (segment.value / total) * 100;
          const isFirst = i === 0;
          const isLast = i === segments.length - 1;
          const style: CSSProperties = {
            width: `${widthPercent}%`,
            background: segment.color,
            borderTopLeftRadius: isFirst ? radius : 0,
            borderBottomLeftRadius: isFirst ? radius : 0,
            borderTopRightRadius: isLast ? radius : 0,
            borderBottomRightRadius: isLast ? radius : 0,
          };
          return <div key={segment.key} style={style} />;
        })}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '10px',
        }}
      >
        {segments.map((segment) => (
          <div key={segment.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: segment.color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '13px', color: tokens.textSecondary }}>
              {segment.label}
              {formatValue ? ` · ${formatValue(segment.value)}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ TrendChart

export interface TrendPoint {
  /** Dia do bucket, em ISO (`2026-07-28`). */
  bucket: string;
  min: number;
  max: number;
  avg: number;
}

export interface TrendChartProps {
  points: TrendPoint[];
  unit?: string;
  height?: number;
  /** Cor da linha da média. Default: accent do produto. */
  stroke?: string;
  formatValue?: (value: number) => string;
}

/**
 * Série temporal diária com FAIXA min–máx + linha da média.
 *
 * Por que faixa e não só a linha: para o médico a amplitude do dia é dado
 * clínico, não ruído a ser suavizado. Uma FC média de 78 esconde tanto
 * "esteve entre 74 e 82" quanto "esteve entre 45 e 165" — e a diferença entre
 * essas duas é justamente o que ele precisa enxergar. A linha sozinha
 * responderia "quanto", a faixa responde "quanto variou", e as duas juntas
 * são a leitura que o dado de wearable pede.
 *
 * USADO NOS DOIS APPS, com propósitos diferentes — e isto mudou em
 * agosto/2026. Antes o componente era exclusivo do painel do médico, porque o
 * app do paciente não mostrava série nenhuma "de propósito" (§3.1/§8.3).
 * Feedback de campo derrubou essa parte: uma paciente com três meses de dado
 * sincronizado descreveu o app como pobre, vendo só o valor do dia.
 *
 * O que NÃO mudou é a fronteira. No painel do médico o gráfico vem com faixa
 * min–máx e limiar; na tela do paciente (`MetricDetail`) ele recebe a série
 * com min = máx = valor, o que colapsa a faixa e deixa só a linha, e nunca
 * recebe faixa de normalidade. O paciente vê o que ele fez comparado com ele
 * mesmo; interpretar continua sendo do médico.
 */
export function TrendChart({
  points,
  unit,
  height = 160,
  stroke = tokens.accent,
  // Inteiro fica como está (a maioria dos min/max já vem assim da RPC); decimal
  // arredonda pra 1 casa — sem isso, a média de um agregado (ex.: 141.166666)
  // aparecia crua no tooltip enquanto min/max ao lado ficavam inteiros.
  formatValue = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1)),
}: TrendChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);

  // Desestruturar em vez de indexar: com `noUncheckedIndexedAccess`, `points[0]`
  // é `TrendPoint | undefined` mesmo depois do guard de length — o compilador
  // não liga uma coisa à outra. O guard abaixo é o mesmo cheque, na forma que
  // o TS consegue estreitar.
  const [first] = points;
  const last = points.at(-1);

  if (!first || !last) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tokens.textSecondary,
          fontSize: '13px',
        }}
      >
        Sem dados nesse período.
      </div>
    );
  }

  const width = 640;
  const padX = 8;
  const padTop = 10;
  const padBottom = 22;
  const plotH = height - padTop - padBottom;

  const lo = Math.min(...points.map((p) => p.min));
  const hi = Math.max(...points.map((p) => p.max));
  // Escala nunca degenera: uma série constante (peso que não mudou na semana)
  // teria lo === hi e dividiria por zero, achatando tudo na borda.
  const span = hi - lo || Math.abs(hi) || 1;
  const padded = span * 0.1;
  const yMin = lo - padded;
  const yMax = hi + padded;

  const x = (i: number) =>
    points.length === 1
      ? width / 2
      : padX + (i * (width - padX * 2)) / (points.length - 1);
  const y = (v: number) => padTop + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

  const bandPath =
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.max)}`).join(' ') +
    ' ' +
    points
      .slice()
      .reverse()
      .map((p, i) => `L${x(points.length - 1 - i)},${y(p.min)}`)
      .join(' ') +
    ' Z';

  const avgPath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.avg)}`)
    .join(' ');

  const fmtDay = (iso: string) => {
    const [, m, d] = iso.split('-');
    return `${d}/${m}`;
  };

  // Índice do ponto mais próximo do mouse, pela posição horizontal — os
  // pontos não têm alvo de clique individual, então a interação inteira
  // depende de mapear a coordenada do mouse na tela de volta para o índice.
  const indexFromClientX = (clientX: number): number => {
    const svg = svgRef.current;
    if (!svg || points.length < 2) return 0;
    const rect = svg.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.min(points.length - 1, Math.max(0, Math.round(ratio * (points.length - 1))));
  };

  const handleMove = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (pinned) return;
    setActiveIndex(indexFromClientX(e.clientX));
  };

  const handleLeave = () => {
    if (!pinned) setActiveIndex(null);
  };

  // Clicar fixa o tooltip no ponto (útil para comparar com o mouse parado);
  // clicar de novo no mesmo ponto solta.
  const handleClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    const idx = indexFromClientX(e.clientX);
    if (pinned && activeIndex === idx) {
      setPinned(false);
      setActiveIndex(null);
    } else {
      setActiveIndex(idx);
      setPinned(true);
    }
  };

  const active = activeIndex !== null ? points[activeIndex] : undefined;
  const activeRatio = active ? x(activeIndex!) / width : 0;
  // Perto das bordas o tooltip centralizado vazaria para fora do card — ancora
  // pela esquerda ou direita nesses trechos em vez de centralizar.
  const activeAlign = activeRatio < 0.15 ? 'left' : activeRatio > 0.85 ? 'right' : 'center';
  const activeTranslateX = activeAlign === 'left' ? '0%' : activeAlign === 'right' ? '-100%' : '-50%';

  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Tendência de ${fmtDay(first.bucket)} a ${fmtDay(last.bucket)}. Mínimo ${formatValue(lo)}, máximo ${formatValue(hi)}.`}
        onMouseMove={points.length > 1 ? handleMove : undefined}
        onMouseLeave={points.length > 1 ? handleLeave : undefined}
        onClick={points.length > 1 ? handleClick : undefined}
        style={{ cursor: points.length > 1 ? 'crosshair' : 'default' }}
      >
        {points.length > 1 && <path d={bandPath} fill={hexToRgba(stroke, 0.16)} />}
        <path
          d={avgPath}
          fill="none"
          stroke={stroke}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {points.length === 1 && <circle cx={x(0)} cy={y(first.avg)} r={3.5} fill={stroke} />}

        {active && (
          <>
            <line
              x1={x(activeIndex!)}
              x2={x(activeIndex!)}
              y1={padTop}
              y2={height - padBottom}
              stroke={tokens.border}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={x(activeIndex!)}
              cy={y(active.avg)}
              r={4}
              fill={stroke}
              stroke={tokens.white}
              strokeWidth={1.5}
            />
          </>
        )}
      </svg>

      {active && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${activeRatio * 100}%`,
            transform: `translate(${activeTranslateX}, -100%)`,
            background: tokens.white,
            border: `1px solid ${tokens.border}`,
            borderRadius: '8px',
            padding: '6px 10px',
            fontFamily: font.stack,
            fontSize: '12px',
            color: tokens.text,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 600 }}>{fmtDay(active.bucket)}</div>
          {active.min === active.max ? (
            <div>
              {formatValue(active.avg)}
              {unit ? ` ${unit}` : ''}
            </div>
          ) : (
            <div style={{ color: tokens.textSecondary }}>
              Mín {formatValue(active.min)} · Média {formatValue(active.avg)} · Máx{' '}
              {formatValue(active.max)}
              {unit ? ` ${unit}` : ''}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: font.stack,
          fontSize: '12px',
          color: tokens.textSecondary,
        }}
      >
        <span>{fmtDay(first.bucket)}</span>
        <span>
          {formatValue(lo)}–{formatValue(hi)}
          {unit ? ` ${unit}` : ''}
        </span>
        <span>{fmtDay(last.bucket)}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ DonutChart

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  /** Texto grande no buraco do donut — normalmente o total. */
  centerValue?: string;
  centerLabel?: string;
}

/**
 * Composição categórica em forma de rosca.
 *
 * Rosca e não torta cheia: o buraco central carrega o total, que é a
 * pergunta que vem antes de "como se divide". Sem ele o total precisaria de
 * uma linha de texto separada, e o gráfico ficaria respondendo só metade.
 *
 * Rótulo direto obrigatório na legenda (regra do skill de dataviz para
 * poucas séries) — nunca só a cor, que exclui quem não distingue matizes.
 * Fatias além da sexta viram "Outros" porque uma rosca com dez fatias deixa
 * de ser leitura e vira decoração.
 */
export function DonutChart({
  slices,
  size = 150,
  thickness = 22,
  centerValue,
  centerLabel,
}: DonutChartProps) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;

  let offset = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        <g transform={`rotate(-90 ${c} ${c})`}>
          {slices.map((slice) => {
            const fraction = slice.value / total;
            const dash = fraction * circumference;
            // Gap de 2px entre fatias, a mesma separação da barra empilhada.
            // Fatia pequena demais para caber o gap fica sem ele em vez de
            // virar um traço negativo.
            const gap = dash > 4 ? 2 : 0;
            const el = (
              <circle
                key={slice.key}
                cx={c}
                cy={c}
                r={r}
                fill="none"
                stroke={slice.color}
                strokeWidth={thickness}
                strokeDasharray={`${Math.max(dash - gap, 0.5)} ${circumference - dash + gap}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return el;
          })}
        </g>
        {centerValue && (
          <text
            x={c}
            y={centerLabel ? c - 2 : c + 6}
            textAnchor="middle"
            fontSize={size * 0.22}
            fontWeight={600}
            fill={tokens.text}
            fontFamily={font.stack}
          >
            {centerValue}
          </text>
        )}
        {centerLabel && (
          <text
            x={c}
            y={c + size * 0.13}
            textAnchor="middle"
            fontSize={size * 0.085}
            fill={tokens.textSecondary}
            fontFamily={font.stack}
          >
            {centerLabel}
          </text>
        )}
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
        {slices.map((slice) => (
          <div
            key={slice.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: font.stack,
              fontSize: '13px',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 9,
                height: 9,
                borderRadius: 3,
                background: slice.color,
                flexShrink: 0,
              }}
            />
            <span style={{ color: tokens.text }}>{slice.label}</span>
            <span style={{ color: tokens.textSecondary }}>{slice.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Sparkline

export interface SparklineProps {
  /** Valores em ordem cronológica. Menos de 2 pontos não desenha linha. */
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  /** Rótulo para leitor de tela — o desenho sozinho não diz nada. */
  label?: string;
}

/**
 * Linha minúscula de tendência, para viver DENTRO de um cartão de métrica.
 *
 * POR QUE NÃO É O `TrendChart` menor. Aquele é para ler: tem eixo, tooltip,
 * faixa min–máx e interação. Este é para RECONHECER de relance — a pergunta
 * que ele responde é "está subindo, descendo ou estável?", e nada além disso.
 * Acrescentar eixo aqui competiria com o número grande ao lado, que é a
 * informação principal do cartão.
 *
 * Nasceu do relatório de UX (ago/2026): "exibição de dados em estado bruto,
 * desprovida de linhas de tendência ou contexto comparativo" gera fadiga de
 * dados — o paciente para de olhar um número que não sabe interpretar. A
 * silhueta da semana resolve isso sem exigir letramento nenhum.
 *
 * SEM COR DE JULGAMENTO, pela mesma razão do resto do app do paciente: a linha
 * é sempre o ouro da marca. Verde para "bom" e vermelho para "ruim" seria
 * juízo clínico entrando pelo gráfico (§3.1).
 */
export function Sparkline({
  values,
  width = 72,
  height = 24,
  stroke = tokens.accent,
  label,
}: SparklineProps) {
  if (values.length < 2) {
    // Um ponto não tem tendência. Espaço reservado mantém o cartão estável
    // quando o dado chega, em vez de empurrar o layout.
    return <div style={{ width, height }} aria-hidden />;
  }

  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // Série constante não pode dividir por zero — vira uma linha no meio.
  const span = hi - lo || 1;

  const pad = 2;
  const x = (i: number) => (i * (width - pad * 2)) / (values.length - 1) + pad;
  const y = (v: number) => height - pad - ((v - lo) / span) * (height - pad * 2);

  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
      {/* O ponto final marca "onde você está agora" — sem ele a linha parece
          um histórico solto, sem presente. */}
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1] as number)}
              r="2.4" fill={stroke} />
    </svg>
  );
}

// ------------------------------------------------------------ RadarChart

export interface RadarAxis {
  key: string;
  label: string;
  /** 0–100. */
  value: number;
}

export interface RadarChartProps {
  axes: RadarAxis[];
  size?: number;
  fillColor?: string;
}

/**
 * Teia (radar) do SB Health Score, um vértice por componente.
 *
 * PENTÁGONO REGULAR — todo eixo com o mesmo comprimento. Uma primeira versão
 * tentou fazer o comprimento do eixo carregar o PESO do componente (sono 25,
 * constância 15…), mas isso deformava a grade de referência e o resultado
 * lido em campo era "ruim", não mais honesto — o objetivo declarado era uma
 * teia limpa e simétrica, com o polígono de valor variando dentro dela.
 * Cada eixo vai de 0 a 100; é a posição do PONTO em cada eixo que conta a
 * história, igual a qualquer radar chart.
 *
 * MESMA REGRA DE COR do resto do app do paciente: hue único, sem escala de
 * severidade por vértice (§3.1) — a forma do polígono é que informa, não a
 * cor de cada ponta.
 */
/** Quebra um rótulo em até duas linhas, pra não estourar a largura do SVG. */
function wrapLabel(label: string): string[] {
  const palavras = label.split(' ');
  if (palavras.length === 1) return [label];
  const meio = Math.ceil(palavras.length / 2);
  return [palavras.slice(0, meio).join(' '), palavras.slice(meio).join(' ')];
}

export function RadarChart({ axes, size = 280, fillColor = tokens.accent }: RadarChartProps) {
  const cx = size / 2;
  const cy = size / 2;
  // Espaço reservado pros rótulos é GENEROSO de propósito: rótulo cortado nas
  // bordas do SVG foi o defeito relatado depois da primeira versão desta
  // teia (screenshot em campo, "SB Health Score" com "Movime…", "…ável"
  // sendo engolidos pelo viewBox). `outerPad` é o raio livre para o texto de
  // cada vértice, então tem que ser grande o bastante pra caber duas linhas.
  const outerPad = size * 0.28;
  const rBase = size / 2 - outerPad;
  const n = axes.length;

  const angleFor = (i: number) => -90 + (360 / n) * i;

  const pointAt = (i: number, fraction: number) => {
    const rad = (angleFor(i) * Math.PI) / 180;
    const r = rBase * fraction;
    return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
  };

  const ringFractions = [0.25, 0.5, 0.75, 1];

  const valuePoints = axes.map((a, i) => pointAt(i, Math.max(0, Math.min(100, a.value)) / 100));
  const valuePath = valuePoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="SB Health Score por componente">
      {/* Anéis de referência, um por eixo (comprimento próprio, não um círculo só). */}
      {ringFractions.map((frac) => {
        const ringPath = axes
          .map((a, i) => {
            const p = pointAt(i, frac);
            return `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`;
          })
          .join(' ') + ' Z';
        return (
          <path key={frac} d={ringPath} fill="none" stroke={tokens.border} strokeWidth="1" />
        );
      })}

      {/* Eixos, todos do mesmo comprimento — pentágono regular. */}
      {axes.map((a, i) => {
        const tip = pointAt(i, 1);
        return (
          <line
            key={a.key}
            x1={cx}
            y1={cy}
            x2={tip.x}
            y2={tip.y}
            stroke={tokens.border}
            strokeWidth="1"
          />
        );
      })}

      {/* Polígono de valores. */}
      <path d={valuePath} fill={hexToRgba(fillColor, 0.18)} stroke={fillColor} strokeWidth="2" strokeLinejoin="round" />
      {valuePoints.map((p, i) => (
        <circle key={axes[i]!.key} cx={p.x} cy={p.y} r="3.5" fill={fillColor} />
      ))}

      {/* Rótulos, na ponta de cada eixo (comprimento cheio, não o valor). */}
      {axes.map((a, i) => {
        const rad = (angleFor(i) * Math.PI) / 180;
        const labelR = rBase + 14;
        const x = cx + labelR * Math.sin(rad);
        const y = cy - labelR * Math.cos(rad);
        const anchor = Math.abs(Math.sin(rad)) < 0.2 ? 'middle' : Math.sin(rad) > 0 ? 'start' : 'end';
        const linhas = wrapLabel(a.label);
        const dyInicial = -((linhas.length - 1) * 6);
        return (
          <text
            key={a.key}
            x={x}
            y={y}
            textAnchor={anchor}
            fontFamily={font.stack}
            fontSize="11"
            fontWeight={600}
            fill={tokens.textSecondary}
          >
            {linhas.map((linha, li) => (
              <tspan key={li} x={x} dy={li === 0 ? dyInicial : 12}>
                {linha}
              </tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
}
